import { planScheduleChanges, validateScheduleChanges } from "./scheduleChanges";
import { getScheduleConfirmation } from "./scheduleConfirmation";
import { planPublishSteps } from "./schedulePublishPlan";
import { ScheduleValue, validateSchedule } from "./scheduleValidation";

const DRAFT = "DRAFT";
const PUBLISHED = "PUBLISHED";
const UNPUBLISHED = "UNPUBLISHED";

/**
 * Everything a content screen has to decide before it touches the backend when the editor presses
 * Publish (KIWI-6576): is the schedule acceptable, which publish calls does it need, and what state
 * should the save that precedes them write. Shared so Article, VOD, Poll, Quiz, Event and the form
 * builder cannot drift apart — Poll/Quiz/Event drifting is exactly how they ended up "scheduling"
 * without scheduling.
 *
 * @param contentType  picks `depublish` (fixed types) vs `unpublish` (schema types) for the plan
 * @param schedule     the editor's current schedule
 * @param currentState the content's stored `page_state`, if any (case-insensitive)
 * @param now          injectable clock, for tests (defaults to the current time)
 * @param existing     the schedules the item already had when it was opened (`scheduleFromTriggers`).
 *                     Those sides are locked in the form, so they are never sent again — the backend
 *                     rejects a second schedule for a side — and only genuinely new sides become steps.
 *                     A publish schedule already in place also means the item must NOT be published
 *                     now. Omitted, the plan is exactly what it was before KIWI-6762.
 */
export const planContentPublish = (
  contentType: string,
  schedule: ScheduleValue,
  currentState?: string | null,
  now?: Date,
  existing?: ScheduleValue | null,
) => {
  const at = now ?? new Date();
  const keptPublish = Boolean(existing?.isSchedulePublish && existing.schedulePublishDateTime);
  const keptUnpublish = Boolean(
    existing?.isScheduleUnpublish && existing.scheduleUnpublishDateTime,
  );
  const verdict = existing
    ? validateScheduleChanges(existing, schedule, at)
    : validateSchedule(schedule, at);
  const newSides = existing ? planScheduleChanges(existing, schedule).creates : schedule;
  let steps = planPublishSteps(contentType, newSides);
  // The kept publish schedule IS the publish: drop the publish-now step the planner adds by default.
  if (keptPublish) steps = steps.filter((step) => step.isSchedule);
  const scheduledPublish = keptPublish || Boolean(steps[0]?.isSchedule);

  // The save that precedes a publish writes `page_state`, and the listing shows it. Saving a
  // SCHEDULED publish as PUBLISHED would label the content Published until its time comes. So a
  // scheduled publish keeps the content's current state (DRAFT when it has none); only a publish
  // that happens now — including "publish now, unpublish later" — saves as PUBLISHED.
  const current = String(currentState ?? "").toUpperCase();
  const keepCurrentState = current === PUBLISHED || current === UNPUBLISHED;
  const saveState = scheduledPublish ? (keepCurrentState ? current : DRAFT) : PUBLISHED;

  return {
    schedule,
    verdict,
    steps,
    // True whenever the item will carry a schedule, new or kept, so call sites take their plan path
    // (which may have no step to send) instead of a plain publish-now.
    isScheduled: keptPublish || keptUnpublish || steps.some((step) => step.isSchedule),
    saveState,
    confirmation: getScheduleConfirmation(schedule),
  };
};

export type ContentPublishPlan = ReturnType<typeof planContentPublish>;
